import React, { useMemo, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import {
  CartesianGrid,
  Cell,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  Briefcase,
  Bug,
  CalendarDays,
  FolderGit2,
  GitBranch,
  LayoutGrid,
  Loader2,
  RefreshCcw,
} from 'lucide-react';
import { ErrorBoundary } from 'react-error-boundary';
import { ErrorFallback } from 'smart-react';

import { Button } from '@/Modules/Core/Components/shadcn/button';
import { DatePicker } from '@/Modules/Core/Components/shadcn/date-picker';
import { Label } from '@/Modules/Core/Components/shadcn/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/Modules/Core/Components/shadcn/select';
import { ChartEmptyState } from '@/Modules/Core/Components/shadcn/chart';
import {
  AppPage,
  AppPageSection,
} from '@/Modules/Core/Components/Layout/AppPage';
import { cn } from '@/lib/utils';

import { DASHBOARD_TILELAYOUT } from '../../../constants/applicationConstants';
import {
  useAffectedFilesByRepos,
  useAnalyticsData,
  useBugsinRolloutAnalytics,
  useRolloutActivities,
  useRolloutProjects,
} from '../Services/DashboardQueries';
import type {
  DateRange,
  DateRangeOption,
  DateRangePreset,
  ISODateString,
  StoredDateFilter,
} from './types';

type DashboardMetric = {
  label: string;
  value: string;
  icon: React.ComponentType<{ className?: string }>;
  description: string;
  accentClassName: string;
  progressClassName: string;
  progressWidth: string;
};

type ActivityPoint = {
  label: string;
  activityCount: number;
  trendCount: number;
  isoDate?: string;
};

type ProjectShareDatum = {
  name: string;
  value: number;
  share: number;
  color: string;
};

type RepositoryDatum = {
  name: string;
  count: number;
  share: number;
  toneClassName: string;
};

type FeedItem = {
  source: string;
  action: string;
  status: 'success' | 'info' | 'warning';
  time: string;
};

const DATE_FILTER_STORAGE_KEY = `${DASHBOARD_TILELAYOUT}_DATE_FILTER`;
const DATE_RANGE_DEFAULT: DateRangePreset = '7';

const PROJECT_COLORS = [
  'oklch(68.5% 0.169 237.323)',
  'oklch(var(--primary))',
  'oklch(69.6% 0.17 162.48)',
  'oklch(76.9% 0.188 70.08)',
  'oklch(63.7% 0.237 25.331)',
  'oklch(71% 0.16 310)',
];

const STATUS_STYLES: Record<FeedItem['status'], string> = {
  success:
    'border-emerald-500/30 bg-emerald-500/10 text-emerald-300 dark:text-emerald-300',
  info: 'border-sky-500/30 bg-sky-500/10 text-sky-300 dark:text-sky-300',
  warning:
    'border-amber-500/30 bg-amber-500/10 text-amber-300 dark:text-amber-300',
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const isDateRangePreset = (value: unknown): value is DateRangePreset =>
  value === '7' || value === '14' || value === '30' || value === 'custom';

const isDateRange = (value: unknown): value is DateRange => {
  if (!isRecord(value)) return false;

  return (
    typeof value.startDate === 'string' && typeof value.endDate === 'string'
  );
};

const toNumber = (value: unknown): number => {
  const parsed = Number(value ?? 0);
  return Number.isFinite(parsed) ? parsed : 0;
};

const clamp = (value: number, min: number, max: number) =>
  Math.min(max, Math.max(min, value));

const formatCount = (value: unknown): string =>
  toNumber(value).toLocaleString('en-US');

const formatMetricValue = (value: unknown): string => {
  if (
    typeof value === 'string' &&
    value.trim() &&
    Number.isNaN(Number(value))
  ) {
    return value;
  }

  return formatCount(value);
};

const parseDateValue = (value?: string | null) => {
  if (!value) return null;

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const formatDateLabel = (value?: string | null): string => {
  const parsed = parseDateValue(value);
  if (!parsed) return value?.trim() || 'Unknown';

  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
  }).format(parsed);
};

const formatCompactCount = (value: unknown): string =>
  new Intl.NumberFormat('en-US', {
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(toNumber(value));

const rangeLabelForFeed = (
  range: DateRangePreset,
  dates: DateRange
): string => {
  if (range === 'custom') {
    return `${formatDateLabel(dates.startDate)} - ${formatDateLabel(dates.endDate)}`;
  }

  return `${range}D window`;
};

const getDefaultDateRange = (days: number = 7): DateRange => {
  const today = new Date();
  const start = new Date(today);
  start.setDate(today.getDate() - days);

  return {
    startDate: start.toISOString().split('T')[0] as ISODateString,
    endDate: today.toISOString().split('T')[0] as ISODateString,
  };
};

const DashboardMetricCard = ({
  label,
  value,
  icon: Icon,
  description,
  accentClassName,
  progressClassName,
  progressWidth,
}: DashboardMetric) => (
  <div className="app-shell-card relative overflow-hidden p-5">
    <div className="absolute inset-x-6 top-0 h-px bg-gradient-to-r from-transparent via-white/50 to-transparent opacity-70" />

    <div className="flex items-start justify-between gap-4">
      <div className="space-y-3">
        <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
          {label}
        </p>
        <div className="text-[28px] font-semibold leading-none tracking-[-0.03em] text-foreground">
          {value}
        </div>
      </div>

      <span
        className={cn(
          'inline-flex h-11 w-11 items-center justify-center rounded-[1rem] border border-border/45 bg-background/55',
          accentClassName
        )}
      >
        <Icon className="h-5 w-5" />
      </span>
    </div>

    <p className="mt-3 text-[13px] leading-6 text-muted-foreground">
      {description}
    </p>

    <div className="mt-5 h-1.5 overflow-hidden rounded-full bg-border/35">
      <div
        className={cn(
          'h-full rounded-full bg-gradient-to-r transition-[width] duration-700',
          progressClassName
        )}
        style={{ width: progressWidth }}
      />
    </div>
  </div>
);

const DashboardPanelSkeleton = ({
  className,
  compact = false,
}: {
  className?: string;
  compact?: boolean;
}) => (
  <div className={cn('app-shell-card overflow-hidden p-5', className)}>
    <div className="space-y-3">
      <div className="h-4 w-32 animate-pulse rounded bg-muted" />
      <div className="h-3 w-52 animate-pulse rounded bg-muted" />
    </div>
    <div
      className={cn(
        'mt-6 animate-pulse rounded-2xl bg-muted/60',
        compact ? 'h-[260px]' : 'h-[320px]'
      )}
    />
  </div>
);

const DashboardMetricSkeleton = () => (
  <div className="app-shell-card overflow-hidden p-5">
    <div className="flex items-start justify-between gap-4">
      <div className="space-y-3">
        <div className="h-3 w-24 animate-pulse rounded bg-muted" />
        <div className="h-8 w-20 animate-pulse rounded bg-muted" />
      </div>
      <div className="h-11 w-11 animate-pulse rounded-2xl bg-muted" />
    </div>
    <div className="mt-4 h-3 w-40 animate-pulse rounded bg-muted" />
    <div className="mt-5 h-1.5 animate-pulse rounded-full bg-muted" />
  </div>
);

const ActivityTooltip = ({
  active,
  payload,
  label,
}: {
  active?: boolean;
  payload?: Array<{ color?: string; name?: string; value?: number }>;
  label?: string;
}) => {
  if (!active || !payload?.length) return null;

  return (
    <div className="rounded-xl border border-border/55 bg-popover/95 px-3 py-2 shadow-lg backdrop-blur">
      <p className="text-[11px] font-medium text-muted-foreground">{label}</p>
      <div className="mt-2 space-y-1">
        {payload.map((item) => (
          <div
            key={`${item.name}-${item.color}`}
            className="flex items-center justify-between gap-6 text-[12px]"
          >
            <span className="flex items-center gap-2">
              <span
                className="h-2.5 w-2.5 rounded-full"
                style={{ backgroundColor: item.color }}
              />
              <span className="text-muted-foreground">{item.name}</span>
            </span>
            <span className="font-medium text-foreground">
              {formatCount(item.value)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

const ProjectsTooltip = ({
  active,
  payload,
}: {
  active?: boolean;
  payload?: Array<{
    name?: string;
    value?: number;
    payload?: ProjectShareDatum;
  }>;
}) => {
  if (!active || !payload?.length) return null;

  const entry = payload[0];
  const project = entry?.payload;

  return (
    <div className="rounded-xl border border-border/55 bg-popover/95 px-3 py-2 shadow-lg backdrop-blur">
      <p className="text-[12px] font-semibold text-foreground">
        {project?.name}
      </p>
      <p className="mt-1 text-[11px] text-muted-foreground">
        {`${formatCount(entry?.value)} rollouts - ${project?.share.toFixed(1) ?? '0.0'}%`}
      </p>
    </div>
  );
};

const Dashboard = () => {
  const queryClient = useQueryClient();
  const stored = useMemo<StoredDateFilter | null>(() => {
    if (typeof window === 'undefined') return null;

    try {
      const raw = window.localStorage.getItem(DATE_FILTER_STORAGE_KEY);
      if (!raw) return null;

      const parsed: unknown = JSON.parse(raw);
      if (!isRecord(parsed)) return null;

      const rangeCandidate = parsed.range;
      const datesCandidate = parsed.dates;

      if (!isDateRange(datesCandidate)) return null;

      return {
        range: isDateRangePreset(rangeCandidate)
          ? rangeCandidate
          : DATE_RANGE_DEFAULT,
        dates: datesCandidate,
      };
    } catch {
      return null;
    }
  }, []);

  const [selectedRange, setSelectedRange] = useState<DateRangePreset>(
    stored?.range ?? DATE_RANGE_DEFAULT
  );
  const [showCalendar, setShowCalendar] = useState<boolean>(
    (stored?.range ?? DATE_RANGE_DEFAULT) === 'custom'
  );
  const [dates, setDates] = useState<DateRange>(
    stored?.dates ?? getDefaultDateRange(7)
  );
  const [isRefreshing, setIsRefreshing] = useState<boolean>(false);

  const updateSelectedDate = (
    value: DateRange,
    range: DateRangePreset
  ): void => {
    if (typeof window === 'undefined') return;

    window.localStorage.setItem(
      DATE_FILTER_STORAGE_KEY,
      JSON.stringify({ range, dates: value } satisfies StoredDateFilter)
    );
  };

  const handleRefresh = async (): Promise<void> => {
    setIsRefreshing(true);
    try {
      await Promise.all([
        queryClient.refetchQueries({
          queryKey: ['analyticsData'],
          exact: false,
        }),
        queryClient.refetchQueries({
          queryKey: ['rolloutActivities'],
          exact: false,
        }),
        queryClient.refetchQueries({
          queryKey: ['rolloutProjectsData'],
          exact: false,
        }),
        queryClient.refetchQueries({
          queryKey: ['affectedFilesData'],
          exact: false,
        }),
        queryClient.refetchQueries({
          queryKey: ['bugsinRollouts'],
          exact: false,
        }),
      ]);
    } finally {
      setIsRefreshing(false);
    }
  };

  const applyRange = (range: DateRangePreset): void => {
    const today = new Date();

    if (range === 'custom') {
      setShowCalendar(true);
      updateSelectedDate(dates, range);
      return;
    }

    const days = range === '14' ? 14 : range === '30' ? 30 : 7;
    const startDate = new Date(today);
    startDate.setDate(today.getDate() - days);

    const nextDates: DateRange = {
      startDate: startDate.toISOString().split('T')[0] as ISODateString,
      endDate: today.toISOString().split('T')[0] as ISODateString,
    };

    setShowCalendar(false);
    setDates(nextDates);
    updateSelectedDate(nextDates, range);
  };

  const handleRangeChange = (value: DateRangePreset): void => {
    setSelectedRange(value);
    applyRange(value);
  };

  const handleDateChange = (field: keyof DateRange, dateObj?: Date): void => {
    if (!dateObj) return;

    const nextDates: DateRange = {
      ...dates,
      [field]: dateObj.toISOString().split('T')[0] as ISODateString,
    };

    setDates(nextDates);
    updateSelectedDate(nextDates, selectedRange);
  };

  const dateRangeOptions: ReadonlyArray<DateRangeOption> = useMemo(
    () => [
      { label: 'Last 7 Days', value: '7' },
      { label: 'Last 14 Days', value: '14' },
      { label: 'Last 30 Days', value: '30' },
      { label: 'Custom Range', value: 'custom' },
    ],
    []
  );

  const { data: metrics, isLoading: isMetricsLoading } = useAnalyticsData();
  const { data: rolloutActivities, isLoading: isActivitiesLoading } =
    useRolloutActivities(dates);
  const { data: rolloutsByProject, isLoading: isProjectsLoading } =
    useRolloutProjects(dates);
  const { data: affectedFiles, isLoading: isAffectedFilesLoading } =
    useAffectedFilesByRepos(dates);
  const { data: bugsData, isLoading: isBugsLoading } =
    useBugsinRolloutAnalytics(dates);

  const activityChartData = useMemo<ActivityPoint[]>(() => {
    const rows = Array.isArray(rolloutActivities) ? rolloutActivities : [];

    const normalized = rows
      .map((item, index) => ({
        isoDate: typeof item?.day === 'string' ? item.day : undefined,
        label: formatDateLabel(
          typeof item?.day === 'string' ? item.day : undefined
        ),
        count: toNumber(item?.count),
        sortIndex: index,
      }))
      .sort((left, right) => {
        const leftDate = parseDateValue(left.isoDate);
        const rightDate = parseDateValue(right.isoDate);

        if (leftDate && rightDate) {
          return leftDate.getTime() - rightDate.getTime();
        }

        return left.sortIndex - right.sortIndex;
      });

    return normalized.map((item, index, array) => {
      const windowStart = Math.max(0, index - 2);
      const rollingWindow = array.slice(windowStart, index + 1);
      const average =
        rollingWindow.reduce((sum, entry) => sum + entry.count, 0) /
        Math.max(rollingWindow.length, 1);

      return {
        label: item.label,
        activityCount: item.count,
        trendCount: Number(average.toFixed(1)),
        isoDate: item.isoDate,
      };
    });
  }, [rolloutActivities]);

  const projectDistributionData = useMemo<ProjectShareDatum[]>(() => {
    const rows = Array.isArray(rolloutsByProject) ? rolloutsByProject : [];
    const normalized = rows
      .map((item, index) => ({
        name:
          typeof item?.project === 'string' && item.project.trim()
            ? item.project.trim()
            : `Project ${index + 1}`,
        value: toNumber(item?.Count),
      }))
      .filter((item) => item.value > 0)
      .sort((left, right) => right.value - left.value);

    const total = normalized.reduce((sum, item) => sum + item.value, 0);

    return normalized.map((item, index) => ({
      ...item,
      share: total > 0 ? (item.value / total) * 100 : 0,
      color: PROJECT_COLORS[index % PROJECT_COLORS.length] ?? PROJECT_COLORS[0],
    }));
  }, [rolloutsByProject]);

  const repositoryData = useMemo<RepositoryDatum[]>(() => {
    const rows = Array.isArray(affectedFiles) ? affectedFiles : [];
    const normalized = rows
      .map((item, index) => ({
        name:
          typeof item?.repo_name === 'string' && item.repo_name.trim()
            ? item.repo_name.trim()
            : `Repository ${index + 1}`,
        count: toNumber(item?.count),
      }))
      .filter((item) => item.count > 0)
      .sort((left, right) => right.count - left.count);

    const total = normalized.reduce((sum, item) => sum + item.count, 0);

    return normalized.map((item, index) => ({
      ...item,
      share: total > 0 ? (item.count / total) * 100 : 0,
      toneClassName:
        index === 0
          ? 'text-sky-300'
          : index === 1
            ? 'text-primary'
            : 'text-emerald-300',
    }));
  }, [affectedFiles]);

  const bugHighlights = useMemo(() => {
    const rows = Array.isArray(bugsData) ? bugsData : [];

    return rows
      .map((item, index) => ({
        rolloutName:
          typeof item?.rollout_name === 'string' && item.rollout_name.trim()
            ? item.rollout_name.trim()
            : `Rollout ${index + 1}`,
        count: toNumber(item?.count),
      }))
      .filter((item) => item.count > 0)
      .sort((left, right) => right.count - left.count);
  }, [bugsData]);

  const repositoryTotalFiles = useMemo(
    () => repositoryData.reduce((sum, item) => sum + item.count, 0),
    [repositoryData]
  );

  const kpis = useMemo<DashboardMetric[]>(() => {
    const totalRollouts = toNumber(metrics?.totalRollouts);
    const totalProjects = Math.max(toNumber(metrics?.totalprojects), 1);
    const uniqueIssues = toNumber(metrics?.uniqueIssues);
    const totalRepos = Math.max(toNumber(metrics?.totalRepos), 1);

    return [
      {
        label: 'Rollouts Published',
        value: formatMetricValue(metrics?.totalRollouts ?? '--'),
        icon: LayoutGrid,
        description:
          'Release artifacts shipped across the selected reporting window.',
        accentClassName: 'text-sky-300',
        progressClassName: 'from-sky-400 via-sky-400 to-primary/80',
        progressWidth: `${clamp(Math.round((totalRollouts / totalProjects) * 100), 14, 100)}%`,
      },
      {
        label: 'Active Projects',
        value: formatMetricValue(metrics?.totalprojects ?? '--'),
        icon: Briefcase,
        description: 'Projects currently participating in rollout operations.',
        accentClassName: 'text-violet-300',
        progressClassName: 'from-violet-400 via-violet-400 to-primary/80',
        progressWidth: `${clamp(Math.round((totalProjects / Math.max(totalRepos, 1)) * 28), 28, 100)}%`,
      },
      {
        label: 'Issue Coverage',
        value: formatMetricValue(metrics?.uniqueIssues ?? '--'),
        icon: Bug,
        description:
          'Distinct issues represented in the release stream right now.',
        accentClassName: 'text-emerald-300',
        progressClassName: 'from-emerald-400 via-emerald-400 to-primary/80',
        progressWidth: `${clamp(Math.round((uniqueIssues / Math.max(totalRollouts, 1)) * 18), 20, 100)}%`,
      },
      {
        label: 'Tracked Repositories',
        value: formatMetricValue(metrics?.totalRepos ?? '--'),
        icon: GitBranch,
        description:
          'Repositories feeding activity, changes, and rollout analytics.',
        accentClassName: 'text-amber-300',
        progressClassName: 'from-amber-400 via-amber-400 to-primary/80',
        progressWidth: `${clamp(Math.round((totalRepos / totalProjects) * 100), 18, 100)}%`,
      },
    ];
  }, [metrics]);

  const feedItems = useMemo<FeedItem[]>(() => {
    const items: FeedItem[] = [];
    const selectedWindow = rangeLabelForFeed(selectedRange, dates);
    const latestActivity = activityChartData[activityChartData.length - 1];
    const topProject = projectDistributionData[0];
    const leadingBug = bugHighlights[0];
    const leadingRepo = repositoryData[0];

    if (latestActivity) {
      items.push({
        source: 'Rollout activity',
        action: `${formatCount(
          latestActivity.activityCount
        )} events recorded for ${latestActivity.label.toLowerCase()}.`,
        status:
          latestActivity.activityCount >= latestActivity.trendCount
            ? 'success'
            : 'info',
        time: latestActivity.label,
      });
    }

    if (topProject) {
      items.push({
        source: topProject.name,
        action: `${formatCount(topProject.value)} rollouts lead the current distribution.`,
        status: topProject.share >= 35 ? 'success' : 'info',
        time: selectedWindow,
      });
    }

    if (leadingBug) {
      items.push({
        source: leadingBug.rolloutName,
        action: `${formatCount(leadingBug.count)} bugs are attached to this rollout.`,
        status: leadingBug.count >= 5 ? 'warning' : 'info',
        time: selectedWindow,
      });
    }

    if (leadingRepo) {
      items.push({
        source: leadingRepo.name,
        action: `${formatCount(leadingRepo.count)} affected files make it the largest repository impact.`,
        status: leadingRepo.share >= 40 ? 'warning' : 'info',
        time: selectedWindow,
      });
    }

    return items.slice(0, 4);
  }, [
    activityChartData,
    bugHighlights,
    dates,
    projectDistributionData,
    repositoryData,
    selectedRange,
  ]);

  return (
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      <div className="tw">
        <AppPage contentClassName="gap-6">
          <AppPageSection className="gap-5">
            <div className="flex flex-col gap-5 xl:flex-row xl:items-start xl:justify-between">
              <div className="space-y-4">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="inline-flex items-center rounded-full border border-emerald-500/20 bg-emerald-500/10 px-3 py-1 text-[12px] font-medium text-emerald-300">
                    {`${formatMetricValue(metrics?.totalprojects ?? '--')} active projects`}
                  </span>
                  <span className="inline-flex items-center rounded-full border border-border/55 bg-card/70 px-3 py-1 text-[12px] font-medium text-muted-foreground">
                    {`${formatMetricValue(metrics?.uniqueIssues ?? '--')} issues in scope`}
                  </span>
                  <span className="inline-flex items-center rounded-full border border-border/55 bg-card/70 px-3 py-1 text-[12px] font-medium text-muted-foreground">
                    {`${formatMetricValue(metrics?.totalRepos ?? '--')} tracked repositories`}
                  </span>
                </div>
              </div>

              <div className="flex flex-col gap-3 xl:items-end">
                <div className="inline-flex flex-wrap items-end gap-3">
                  {showCalendar ? (
                    <div className="flex w-full flex-col gap-3 sm:w-auto sm:flex-row sm:items-end">
                      <div className="flex w-full flex-col gap-2 sm:w-44">
                        <Label className="px-1 text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                          Start Date
                        </Label>
                        <DatePicker
                          value={
                            dates.startDate
                              ? new Date(dates.startDate)
                              : undefined
                          }
                          onChange={(date) =>
                            handleDateChange('startDate', date)
                          }
                          placeholder="Start date"
                          className="h-10 rounded-xl border-border/45 bg-background/70 px-3 text-[13px] shadow-none before:hidden after:hidden hover:translate-y-0 hover:border-border/45 hover:bg-background/70 hover:text-foreground hover:shadow-none"
                        />
                      </div>
                      <div className="flex w-full flex-col gap-2 sm:w-44">
                        <Label className="px-1 text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                          End Date
                        </Label>
                        <DatePicker
                          value={
                            dates.endDate ? new Date(dates.endDate) : undefined
                          }
                          onChange={(date) => handleDateChange('endDate', date)}
                          placeholder="End date"
                          className="h-10 rounded-xl border-border/45 bg-background/70 px-3 text-[13px] shadow-none before:hidden after:hidden hover:translate-y-0 hover:border-border/45 hover:bg-background/70 hover:text-foreground hover:shadow-none"
                        />
                      </div>
                    </div>
                  ) : null}
                  <div className="min-w-[220px] flex-1">
                    <Select
                      value={selectedRange}
                      onValueChange={handleRangeChange}
                    >
                      <SelectTrigger className="h-10 rounded-xl border-border/45 bg-background/70 pl-4 pr-3 text-[14px] shadow-none hover:border-border/45 hover:bg-background/70 hover:text-foreground">
                        <div className="flex min-w-0 items-center gap-3">
                          <CalendarDays className="h-4 w-4 shrink-0 text-muted-foreground" />
                          <SelectValue />
                        </div>
                      </SelectTrigger>
                      <SelectContent align="end">
                        {dateRangeOptions.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="flex items-center gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="icon-lg"
                      className={cn(
                        'rounded-xl border-border/45 bg-background/70 text-muted-foreground shadow-none before:hidden after:hidden hover:translate-y-0 hover:border-border/45 hover:bg-background/70 hover:text-foreground hover:shadow-none',
                        isRefreshing && 'text-primary'
                      )}
                      onClick={handleRefresh}
                      disabled={isRefreshing}
                      aria-label="Refresh dashboard data"
                    >
                      {isRefreshing ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <RefreshCcw className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </AppPageSection>

          <AppPageSection className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
            {isMetricsLoading
              ? Array.from({ length: 4 }).map((_, index) => (
                  <DashboardMetricSkeleton key={index} />
                ))
              : kpis.map((kpi) => (
                  <DashboardMetricCard key={kpi.label} {...kpi} />
                ))}
          </AppPageSection>

          <AppPageSection className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,1fr)_336px]">
            {isActivitiesLoading ? (
              <DashboardPanelSkeleton />
            ) : (
              <div
                id="dashboard-recent-activity"
                className="app-shell-card overflow-hidden p-5"
              >
                <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
                  <div>
                    <h2 className="text-[14px] font-semibold text-foreground">
                      Rollout Activity
                    </h2>
                    <p className="text-[13px] text-muted-foreground">
                      Activity count across the selected range.
                    </p>
                  </div>
                  <div className="flex flex-wrap items-center gap-4 text-[11px] text-muted-foreground">
                    <span className="inline-flex items-center gap-2">
                      <span className="h-[2px] w-4 rounded-full bg-amber-400" />
                      Activities
                    </span>
                  </div>
                </div>

                <div className="mt-5 h-[260px] cursor-pointer">
                  {activityChartData.length ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart
                        data={activityChartData}
                        margin={{ top: 10, right: 6, bottom: 0, left: -6 }}
                        style={{ cursor: 'pointer' }}
                      >
                        <CartesianGrid
                          stroke="oklch(var(--border) / 0.4)"
                          strokeDasharray="3 5"
                          vertical
                        />
                        <XAxis
                          dataKey="label"
                          tick={{
                            fill: 'oklch(var(--muted-foreground))',
                            fontSize: 11,
                          }}
                          axisLine={false}
                          tickLine={false}
                          dy={8}
                        />
                        <YAxis
                          allowDecimals={false}
                          tickFormatter={formatCompactCount}
                          tick={{
                            fill: 'oklch(var(--muted-foreground))',
                            fontSize: 11,
                          }}
                          axisLine={false}
                          tickLine={false}
                          width={42}
                        />
                        <Tooltip content={<ActivityTooltip />} />
                        <Line
                          type="monotoneX"
                          dataKey="activityCount"
                          name="Activities"
                          stroke="#f59e0b"
                          strokeWidth={3}
                          style={{ cursor: 'pointer' }}
                          dot={{
                            r: 4.5,
                            fill: 'oklch(var(--card))',
                            stroke: '#f59e0b',
                            strokeWidth: 2.5,
                          }}
                          activeDot={{
                            r: 6,
                            fill: '#f59e0b',
                            stroke: 'oklch(var(--card))',
                            strokeWidth: 3,
                          }}
                          isAnimationActive={false}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  ) : (
                    <ChartEmptyState
                      title="No activity"
                      description="No rollout activity was returned for the selected date range."
                      className="h-full"
                    />
                  )}
                </div>
              </div>
            )}

            {isProjectsLoading ? (
              <DashboardPanelSkeleton compact />
            ) : (
              <div className="app-shell-card flex min-h-[360px] flex-col overflow-hidden p-5">
                <div>
                  <h2 className="text-[14px] font-semibold text-foreground">
                    Rollouts by Project
                  </h2>
                  <p className="text-[13px] text-muted-foreground">
                    Distribution of rollout volume across active projects.
                  </p>
                </div>

                <div className="mt-4 flex flex-1 items-center justify-center cursor-pointer">
                  {projectDistributionData.length ? (
                    <div className="relative h-[210px] w-full">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={projectDistributionData}
                            dataKey="value"
                            nameKey="name"
                            innerRadius={44}
                            outerRadius={88}
                            paddingAngle={4}
                            cornerRadius={10}
                            strokeWidth={0}
                            style={{ cursor: 'pointer' }}
                          >
                            {projectDistributionData.map((entry) => (
                              <Cell
                                key={entry.name}
                                fill={entry.color}
                                style={{ cursor: 'pointer' }}
                              />
                            ))}
                          </Pie>
                          <Tooltip content={<ProjectsTooltip />} />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  ) : (
                    <ChartEmptyState
                      title="No projects"
                      description="No rollouts were returned for the selected date range."
                      className="h-full"
                    />
                  )}
                </div>

                {projectDistributionData.length ? (
                  <div className="mt-3 space-y-3">
                    {projectDistributionData.slice(0, 4).map((item) => (
                      <div
                        key={item.name}
                        className="flex items-center justify-between gap-3"
                      >
                        <div className="flex min-w-0 items-center gap-3">
                          <span
                            className="h-2.5 w-2.5 shrink-0 rounded-[3px]"
                            style={{ backgroundColor: item.color }}
                          />
                          <span
                            className="truncate text-[13px] text-muted-foreground"
                            title={item.name}
                          >
                            {item.name}
                          </span>
                        </div>
                        <span className="shrink-0 text-[11px] font-medium text-muted-foreground">
                          {`${item.share.toFixed(1)}%`}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : null}
              </div>
            )}
          </AppPageSection>

          <AppPageSection className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,1fr)_336px]">
            {isActivitiesLoading ||
            isProjectsLoading ||
            isAffectedFilesLoading ||
            isBugsLoading ? (
              <DashboardPanelSkeleton />
            ) : (
              <div className="app-shell-card overflow-hidden p-5">
                <div className="flex flex-col gap-2 border-b border-border/35 pb-4 sm:flex-row sm:items-end sm:justify-between">
                  <div>
                    <h2 className="text-[14px] font-semibold text-foreground">
                      Recent Activity
                    </h2>
                    <p className="text-[13px] text-muted-foreground">
                      API-derived highlights from rollout, bug, and repository
                      data.
                    </p>
                  </div>
                  <div className="text-[11px] font-medium uppercase tracking-[0.16em] text-muted-foreground">
                    Live snapshot
                  </div>
                </div>

                {feedItems.length ? (
                  <>
                    <div className="mt-4 overflow-x-auto">
                      <div className="min-w-[640px]">
                        <div className="grid grid-cols-[minmax(0,1.1fr)_minmax(0,1.7fr)_88px_96px] gap-3 px-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
                          <div>Source</div>
                          <div>Action</div>
                          <div>Status</div>
                          <div className="text-right">Time</div>
                        </div>

                        <div className="mt-2">
                          {feedItems.map((item, index) => (
                            <div
                              key={`${item.source}-${item.action}`}
                              className={cn(
                                'grid grid-cols-[minmax(0,1.1fr)_minmax(0,1.7fr)_88px_96px] gap-3 rounded-xl px-1 py-3 transition-colors hover:bg-primary/5',
                                index < feedItems.length - 1 &&
                                  'border-b border-border/25'
                              )}
                            >
                              <div
                                className="truncate text-[13px] font-medium text-foreground"
                                title={item.source}
                              >
                                {item.source}
                              </div>
                              <div
                                className="text-[13px] text-muted-foreground"
                                title={item.action}
                              >
                                {item.action}
                              </div>
                              <div>
                                <span
                                  className={cn(
                                    'inline-flex items-center rounded-md border px-2 py-1 text-[11px] font-medium capitalize',
                                    STATUS_STYLES[item.status]
                                  )}
                                >
                                  {item.status}
                                </span>
                              </div>
                              <div className="text-right text-[11px] text-muted-foreground">
                                {item.time}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>

                    <p className="mt-3 text-right text-[12px] text-muted-foreground">
                      {`Showing ${feedItems.length} API-derived signals`}
                    </p>
                  </>
                ) : (
                  <div className="mt-5 h-[240px]">
                    <ChartEmptyState
                      title="No highlights"
                      description="The selected APIs did not return enough data to build the activity feed."
                      className="h-full"
                    />
                  </div>
                )}
              </div>
            )}

            {isAffectedFilesLoading ? (
              <DashboardPanelSkeleton compact />
            ) : (
              <div className="app-shell-card overflow-hidden p-5">
                <div>
                  <h2 className="text-[14px] font-semibold text-foreground">
                    Repositories
                  </h2>
                  <p className="text-[13px] text-muted-foreground">
                    Repository impact across affected files returned by the API.
                  </p>
                </div>

                {repositoryData.length ? (
                  <div className="mt-4 space-y-3">
                    {repositoryData.slice(0, 4).map((repo) => (
                      <div
                        key={repo.name}
                        className="rounded-2xl border border-border/40 bg-background/45 p-4 transition-colors hover:bg-primary/5"
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex min-w-0 items-start gap-3">
                            <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-2xl border border-border/45 bg-card/75 text-muted-foreground">
                              <FolderGit2 className="h-4 w-4" />
                            </span>

                            <div className="min-w-0">
                              <div
                                className="truncate font-mono text-[13px] font-medium text-foreground"
                                title={repo.name}
                              >
                                {repo.name}
                              </div>
                              <div className="mt-1 text-[11px] text-muted-foreground">
                                {`${formatCount(repo.count)} affected files`}
                              </div>
                            </div>
                          </div>

                          <span
                            className={cn(
                              'shrink-0 text-[11px] font-semibold',
                              repo.toneClassName
                            )}
                          >
                            {`${repo.share.toFixed(1)}%`}
                          </span>
                        </div>

                        <div className="mt-3 h-2 overflow-hidden rounded-full bg-border/30">
                          <div
                            className="h-full rounded-full bg-gradient-to-r from-sky-400 via-primary to-emerald-400"
                            style={{
                              width: `${clamp(Math.round(repo.share), 8, 100)}%`,
                            }}
                          />
                        </div>

                        <div className="mt-2 flex items-center justify-between text-[11px] text-muted-foreground">
                          <span>Share of repository impact</span>
                          <span>{`${formatCount(repositoryTotalFiles)} total files`}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="mt-5 h-[240px]">
                    <ChartEmptyState
                      title="No repositories"
                      description="No repository impact was returned for the selected date range."
                      className="h-full"
                    />
                  </div>
                )}
              </div>
            )}
          </AppPageSection>
        </AppPage>
      </div>
    </ErrorBoundary>
  );
};

export default Dashboard;
